import { ThemeProvider } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PreloadedState } from '@reduxjs/toolkit';
import { render, RenderOptions } from '@testing-library/react';
import { Form, Formik, FormikConfig } from 'formik';
import { PropsWithChildren } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppStore, RootState, setupStore } from 'theSource/app/store';
import { Snackbar } from 'theSource/components';
import { getTheme } from 'theSource/theme';

interface ExtendedRenderOptions extends Omit<RenderOptions, 'queries'> {
  preloadedState?: PreloadedState<RootState>;
  store?: AppStore;
  renderWithoutRouter?: boolean;
}

const testQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0,
      retry: 0,
    },
  },
});

export const renderWithWrappers = (
  ui: React.ReactElement,
  {
    preloadedState = {},
    store = setupStore(preloadedState),
    renderWithoutRouter = false,
    ...renderOptions
  }: ExtendedRenderOptions = {}
) => {
  const theme = getTheme();
  const Wrapper = ({ children }: PropsWithChildren<{}>): JSX.Element => (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <QueryClientProvider client={testQueryClient}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            {renderWithoutRouter ? (
              <>
                {children}
                <Snackbar />
              </>
            ) : (
              <BrowserRouter>
                {children}
                <Snackbar />
              </BrowserRouter>
            )}
          </LocalizationProvider>
        </QueryClientProvider>
      </Provider>
    </ThemeProvider>
  );

  return {
    store,
    ...render(ui, { wrapper: Wrapper, ...renderOptions }),
  };
};

export const withFormik = <T,>(
  formikProps: FormikConfig<T>,
  component: React.ReactNode
) => (
  <Formik {...formikProps}>
    {() => (
      <Form>
        {component}
        <button data-testid="form-submit" type="submit">
          Submit
        </button>
      </Form>
    )}
  </Formik>
);
